import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, Link} from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Form,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
} from 'semantic-ui-react';
import {getSample, rateSample, revokeContact} from './api';

const Page = styled.div`
  padding: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const ChapterText = styled.div`
  font-size: 1.05em;
  line-height: 1.7;
  white-space: pre-wrap;
  margin-top: 16px;
`;

const StickyCta = styled.div`
  position: sticky;
  bottom: 0;
  padding: 12px;
  background: var(--br-surface);
  border-top: 1px solid var(--br-border);
  text-align: center;
  z-index: 10;
`;

const Note = styled.div`
  font-size: 0.85em;
  color: var(--br-muted);
  margin-top: 8px;
`;

const SampleChapterReader = () => {
  const {id} = useParams();
  const idToken = useSelector((s) => s.user.idToken);
  const [sample, setSample] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState(null); // 'yes' | 'no'
  const [contactOptIn, setContactOptIn] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(Date.now());

  const load = async () => {
    try {
      const s = await getSample(idToken, id);
      setSample(s);
      if (s.myRating) {
        setContactOptIn(
          !!s.myRating.contactOptIn && !s.myRating.contactRevokedAt
        );
        setComment(s.myRating.comment || '');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (idToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, id]);

  const openAnswerModal = (answer) => {
    setPendingAnswer(answer);
    setModalOpen(true);
  };

  const submitRating = async () => {
    if (!pendingAnswer) return;
    setBusy(true);
    try {
      const wantsToContinue = pendingAnswer === 'yes';
      await rateSample(idToken, id, {
        wantsToContinue,
        contactOptIn: wantsToContinue && contactOptIn,
        comment: comment || undefined,
        readSeconds: Math.round((Date.now() - startedAt.current) / 1000),
      });
      await load();
      setModalOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeContact = async () => {
    setBusy(true);
    try {
      await revokeContact(idToken, id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sample && !error) return <Loader active />;
  if (error) return <Message error content={error} />;
  if (!sample) return null;

  const myRating = sample.myRating;
  const optedIn = myRating && myRating.contactOptIn && !myRating.contactRevokedAt;

  return (
    <Page>
      <Button as={Link} to="/sample-chapters/discover" basic size="small">
        <Icon name="arrow left" /> Back to samples
      </Button>
      <h1>{sample.title}</h1>
      <div>
        by {sample.authorName || 'Unknown'}{' '}
        <Label>Variant {sample.variantLabel}</Label>{' '}
        <Label size="tiny">
          <Icon name="font" /> Text
        </Label>
        {sample.audioUrl ? (
          <Label color="violet" size="tiny">
            <Icon name="volume up" /> Audio
          </Label>
        ) : (
          <Label size="tiny" basic>
            No audio
          </Label>
        )}
      </div>
      {sample.description && <p>{sample.description}</p>}

      {sample.audioUrl && (
        <Segment>
          <strong>
            <Icon name="volume up" /> Listen
          </strong>
          <audio controls src={sample.audioUrl} style={{width: '100%'}} />
        </Segment>
      )}

      <ChapterText>{sample.contentText}</ChapterText>

      {myRating ? (
        <Segment>
          <h3>Your rating</h3>
          <p>
            You said{' '}
            <strong>
              {myRating.wantsToContinue ? 'yes' : 'no'}
            </strong>{' '}
            — {myRating.wantsToContinue
              ? 'you would keep reading.'
              : 'you would not keep reading.'}
          </p>
          {optedIn && (
            <Message info>
              You agreed to be contacted by the author when more material is
              ready. Your contact information will be shared with the author.{' '}
              <Button basic size="mini" onClick={handleRevokeContact} disabled={busy}>
                Revoke and remove my contact
              </Button>
            </Message>
          )}
          {!optedIn && myRating.contactRevokedAt && (
            <Message>You revoked your contact opt-in.</Message>
          )}
          <Button basic size="small" onClick={() => openAnswerModal(myRating.wantsToContinue ? 'yes' : 'no')}>
            <Icon name="edit" /> Update rating
          </Button>
        </Segment>
      ) : (
        <StickyCta>
          <strong>Would you keep reading?</strong>
          <div style={{marginTop: 8}}>
            <Button positive onClick={() => openAnswerModal('yes')}>
              <Icon name="thumbs up" /> Yes
            </Button>
            <Button onClick={() => openAnswerModal('no')}>
              <Icon name="thumbs down" /> No
            </Button>
          </div>
        </StickyCta>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="small">
        <Modal.Header>
          {pendingAnswer === 'yes'
            ? 'Glad you would keep reading!'
            : 'Thanks for your honest answer'}
        </Modal.Header>
        <Modal.Content>
          {pendingAnswer === 'yes' && (
            <Form>
              <Form.Field>
                <Checkbox
                  checked={contactOptIn}
                  onChange={(_, d) => setContactOptIn(!!d.checked)}
                  label="Yes, let the author contact me when more material is ready"
                />
                <Note>
                  * If you opt in, your contact information (name and email
                  associated with your account) will be shared with the author
                  of this sample. You can revoke at any time from this page.
                </Note>
              </Form.Field>
              <Form.Field>
                <label>Optional comment to the author</label>
                <textarea
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Form.Field>
            </Form>
          )}
          {pendingAnswer === 'no' && (
            <Form>
              <Form.Field>
                <label>Optional: tell the author why (won't be shown publicly)</label>
                <textarea
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Form.Field>
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button primary onClick={submitRating} disabled={busy}>
            {busy ? 'Saving…' : 'Submit'}
          </Button>
        </Modal.Actions>
      </Modal>
    </Page>
  );
};

export default SampleChapterReader;
